import { useApplication, useTick } from '@pixi/react';
import type { Sprite, Ticker } from 'pixi.js';
import { useCallback, useEffect, useRef } from 'react';
import { designUnit } from '@/lib/designUnit';
import { useReducedMotion } from '@/lib/useReducedMotion';
import { glowTexture } from '../glowTexture';

interface Mote {
  /** Position as a fraction of the stage. */
  x: number;
  y: number;
  /** Rise speed in stage heights per second. */
  speed: number;
  phase: number;
  /** Diameter in design units. */
  size: number;
  alpha: number;
}

function createMote(y = Math.random()): Mote {
  return {
    x: Math.random(),
    y,
    speed: 0.006 + Math.random() * 0.012,
    phase: Math.random() * Math.PI * 2,
    size: 3 + Math.random() * 6,
    alpha: 0.12 + Math.random() * 0.3,
  };
}

/** Faint glowing dust drifting upward through the room, in stage (screen) space. */
export function DustMotes({ count = 24, color = 0x9fdcff }: { count?: number; color?: number }) {
  const { app } = useApplication();
  const sprites = useRef<(Sprite | null)[]>([]);
  const motes = useRef<Mote[]>([]);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    motes.current = Array.from({ length: count }, () => createMote());
  }, [count]);

  const animate = useCallback(
    (ticker: Ticker) => {
      const { width, height } = app.screen;
      const u = designUnit(width, height);
      const dt = ticker.deltaMS / 1000;
      const t = ticker.lastTime / 1000;
      motes.current.forEach((mote, i) => {
        if (!reducedMotion) {
          mote.y -= mote.speed * dt;
          if (mote.y < -0.05) Object.assign(mote, createMote(1.05));
        }
        const sprite = sprites.current[i];
        if (!sprite) return;
        sprite.x = (mote.x + Math.sin(t * 0.4 + mote.phase) * 0.012) * width;
        sprite.y = mote.y * height;
        sprite.width = sprite.height = mote.size * u;
        sprite.alpha = mote.alpha * (0.6 + 0.4 * Math.sin(t * 1.7 + mote.phase));
      });
    },
    [app, reducedMotion],
  );
  useTick(animate);

  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <pixiSprite
          key={i}
          ref={(sprite: Sprite | null) => {
            sprites.current[i] = sprite;
          }}
          texture={glowTexture()}
          anchor={0.5}
          tint={color}
          alpha={0}
          blendMode="add"
        />
      ))}
    </>
  );
}
